// src/components/tuition/TuitionOwnerDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaMoneyBillWave,
  FaVideo,
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaWhatsapp,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaEye,
  FaCopy,
  FaBuilding,
  FaSlidersH,
  FaFileInvoiceDollar,
  FaExternalLinkAlt,
  FaPlay,
  FaShareAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../../context/AppThemeContext";
import { useTenant } from "../../context/TenantContext";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import {
  getStoredStudents,
  saveStoredStudents,
  getStoredTeachers,
  saveStoredTeachers,
  getStoredSalaries,
  saveStoredSalaries,
  getStoredOnlineClasses,
  saveStoredOnlineClasses,
  getStoredTuitionInstitutes,
  saveStoredTuitionInstitutes,
  TUITION_MODULE_KEYS,
  getMeetingAttendanceLogs,
} from "./tuitionMockData";
import { normalizeVisibleOnlineClasses } from "./tuitionOnlineClass";
import ClassroomManagementStudio from "./ClassroomManagementStudio";

export default function TuitionOwnerDashboard() {
  const { isDarkMode } = useAppTheme();
  const { activeTenant, tenantFeatures } = useTenant();
  const navigate = useNavigate();

  // Load active tuition institute (default to first one)
  const institutes = useMemo(() => activeTenant ? [activeTenant] : [], [activeTenant]);
  const [selectedInstituteId, setSelectedInstituteId] = useState(() => activeTenant?.id || "");
  const currentInstitute = activeTenant;

  // Read enabled modules for this tuition
  const enabledModules = tenantFeatures?.enabledModules || currentInstitute?.enabledModules || {};
  const studentsEnabled = enabledModules[TUITION_MODULE_KEYS.STUDENT_MANAGEMENT] === true;
  const teachersEnabled = enabledModules[TUITION_MODULE_KEYS.TEACHER_MANAGEMENT] === true;
  const financialsEnabled = enabledModules[TUITION_MODULE_KEYS.FEES_FINANCIALS] === true;
  const onlineClassesEnabled = enabledModules[TUITION_MODULE_KEYS.ONLINE_CLASSES] === true;

  // Active Tab: "analytics" | "students" | "teachers" | "financials" | "classes"
  const [activeTab, setActiveTab] = useState("analytics");

  // Data States
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [activeStudioClass, setActiveStudioClass] = useState(null);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [dashboardSummary, setDashboardSummary] = useState(null);

  // Load tenant-scoped records from the backend. The server derives the tenant
  // from the authenticated session; no browser-selected tenant ID is sent.
  useEffect(() => {
    let active = true;
    const list = (response, keys) => {
      const payload = response?.data?.data ?? response?.data;
      if (Array.isArray(payload)) return payload;
      for (const key of keys) if (Array.isArray(payload?.[key])) return payload[key];
      return [];
    };
    Promise.all([
      studentsEnabled ? api.get(API_PATHS.tuition.students()) : Promise.resolve(null),
      teachersEnabled ? api.get(API_PATHS.tuition.teachers()) : Promise.resolve(null),
      financialsEnabled ? api.get(API_PATHS.tuition.salaries()) : Promise.resolve(null),
      onlineClassesEnabled ? api.get(API_PATHS.tuition.onlineClasses()) : Promise.resolve(null),
      financialsEnabled ? api.get(API_PATHS.tuition.dashboard()) : Promise.resolve(null),
    ]).then(([studentResponse, teacherResponse, salaryResponse, classResponse, dashboardResponse]) => {
      if (!active) return;
      setStudents(list(studentResponse, ["students", "items"]));
      setTeachers(list(teacherResponse, ["teachers", "items"]));
      setSalaries(list(salaryResponse, ["salaries", "items"]));
      setOnlineClasses(normalizeVisibleOnlineClasses(list(classResponse, ["rooms", "classes", "items"])));
      setDashboardSummary(dashboardResponse?.data?.data ?? dashboardResponse?.data ?? null);
      setAttendanceLogs([]);
    }).catch((error) => toast.error(error?.message || "Unable to load tuition workspace data."));
    return () => { active = false; };
  }, [studentsEnabled, teachersEnabled, financialsEnabled, onlineClassesEnabled]);

  // Filters & Search
  const [studentSearch, setStudentSearch] = useState("");
  const [studentBatchFilter, setStudentBatchFilter] = useState("ALL");
  const [teacherSearch, setTeacherSearch] = useState("");

  // Modals
  const [studentModal, setStudentModal] = useState({ open: false, isEditing: false, data: null });
  const [teacherModal, setTeacherModal] = useState({ open: false, isEditing: false, data: null });
  const [viewStudentModal, setViewStudentModal] = useState({ open: false, student: null });
  const [salaryModal, setSalaryModal] = useState({ open: false, teacher: null });
  const [reminderModal, setReminderModal] = useState({ open: false, student: null, messageText: "" });
  const [scheduleClassModal, setScheduleClassModal] = useState({ open: false });

  // Student Form State
  const [studentForm, setStudentForm] = useState({
    name: "",
    rollNo: "",
    email: "",
    mobile: "",
    batch: "",
    grade: "",
    subjects: "",
    parentName: "",
    parentMobile: "",
    parentRelation: "Father",
    totalFee: "",
    paidFee: "",
    dueDate: "",
  });

  // Teacher Form State
  const [teacherForm, setTeacherForm] = useState({
    name: "",
    email: "",
    phone: "",
    specialization: "",
    assignedBatches: "",
    monthlySalary: "",
    qualification: "",
  });

  // Salary Payment Form State
  const [salaryForm, setSalaryForm] = useState({
    month: "",
    amount: "",
    paymentMode: "Bank NEFT",
    referenceId: "",
  });

  // Online Class Form State
  const [classForm, setClassForm] = useState({
    topic: "",
    subject: "",
    batch: "",
    teacherName: "",
    scheduledDate: "",
    startTime: "",
    endTime: "",
  });

  // Financial Metrics
  const financialSummary = useMemo(() => {
    const totalBilled = Number(dashboardSummary?.totalBilled ?? 0);
    const totalCollected = Number(dashboardSummary?.collected ?? 0);
    const totalPending = Number(dashboardSummary?.pending ?? 0);
    const totalSalaryBudget = teachers.reduce((sum, t) => sum + Number(t.monthlySalary || 0), 0);
    const totalSalaryPaid = Number(dashboardSummary?.salaryDisbursed ?? 0);
    const netTuitionMargin = Number(dashboardSummary?.netMargin ?? 0);
    const salaryPercentage = dashboardSummary?.facultySalaryPercent == null ? null : Number(dashboardSummary.facultySalaryPercent);
    const marginPercentage = dashboardSummary?.operatingMarginPercent == null ? null : Number(dashboardSummary.operatingMarginPercent);

    return {
      totalBilled,
      totalCollected,
      totalPending,
      totalSalaryBudget,
      totalSalaryPaid,
      netTuitionMargin,
      salaryPercentage,
      marginPercentage,
    };
  }, [dashboardSummary, teachers]);

  // Filtered Students
  const filteredStudents = useMemo(() => {
    const query = studentSearch.trim().toLowerCase();
    return (students || []).filter((s) => {
      const matchSearch =
        (s.name || "").toLowerCase().includes(query) ||
        (s.rollNo || "").toLowerCase().includes(query) ||
        (s.parentName || "").toLowerCase().includes(query);
      const matchBatch = studentBatchFilter === "ALL" || s.batch === studentBatchFilter;
      return matchSearch && matchBatch;
    });
  }, [students, studentSearch, studentBatchFilter]);
  const batchOptions = useMemo(
    () => [...new Set((students || []).map((student) => student.batch).filter(Boolean))],
    [students]
  );

  // Filtered Teachers
  const filteredTeachers = useMemo(() => {
    const query = teacherSearch.trim().toLowerCase();
    return (teachers || []).filter((t) => {
      const matchName = (t.name || "").toLowerCase().includes(query);
      const specs = Array.isArray(t.specialization)
        ? t.specialization
        : typeof t.specialization === "string"
        ? t.specialization.split(",").map((s) => s.trim())
        : [];
      const matchSpec = specs.some((spec) => (spec || "").toLowerCase().includes(query));
      return matchName || matchSpec;
    });
  }, [teachers, teacherSearch]);

  // -------------------------------------------------------------
  // STUDENT CRUD HANDLERS
  // -------------------------------------------------------------
  const openNewStudentModal = () => {
    navigate("/tuition/students/new");
  };

  const openEditStudentModal = (student) => {
    navigate(`/tuition/students/${encodeURIComponent(student.id)}/edit`);
  };

  const handleSaveStudent = (e) => {
    e.preventDefault();
    if (!studentForm.name.trim()) {
      toast.error("Student name is required.");
      return;
    }

    const total = Number(studentForm.totalFee || 0);
    const paid = Number(studentForm.paidFee || 0);
    const remaining = Math.max(0, total - paid);
    const feeStatus = remaining === 0 ? "PAID" : paid === 0 ? "OVERDUE" : "PARTIAL";

    const subjectsArray = studentForm.subjects
      ? studentForm.subjects.split(",").map((s) => s.trim()).filter(Boolean)
      : ["General"];

    let updatedList;
    if (studentModal.isEditing && studentModal.data) {
      updatedList = students.map((s) => {
        if (s.id === studentModal.data.id) {
          return {
            ...s,
            ...studentForm,
            subjects: subjectsArray,
            totalFee: total,
            paidFee: paid,
            remainingFee: remaining,
            feeStatus,
          };
        }
        return s;
      });
      toast.success("Student admission record updated.");
    } else {
      const newStudent = {
        id: `stu-${Date.now()}`,
        tuitionId: currentInstitute.id,
        ...studentForm,
        subjects: subjectsArray,
        totalFee: total,
        paidFee: paid,
        remainingFee: remaining,
        feeStatus,
        attendanceRate: 100,
        admissionDate: new Date().toISOString().split("T")[0],
        recentMarks: [],
      };
      updatedList = [newStudent, ...students];
      toast.success("New student enrolled successfully!");
    }

    setStudents(updatedList);
    saveStoredStudents(updatedList);
    setStudentModal({ open: false, isEditing: false, data: null });
  };

  const handleDeleteStudent = async (studentId) => {
    const confirm = window.confirm("Are you sure you want to remove this student record?");
    if (!confirm) return;
    try {
      await api.delete(API_PATHS.tuition.studentById(studentId));
      setStudents((current) => current.filter((s) => s.id !== studentId));
      toast.info("Student record removed.");
    } catch (error) { toast.error(error?.message || "Unable to remove this student."); }
  };

  // -------------------------------------------------------------
  // TEACHER CRUD HANDLERS
  // -------------------------------------------------------------
  const openNewTeacherModal = () => {
    navigate("/tuition/teachers/new");
  };

  const openEditTeacherModal = (teacher) => {
    navigate(`/tuition/teachers/${encodeURIComponent(teacher.id)}/edit`);
  };

  const handleSaveTeacher = (e) => {
    e.preventDefault();
    if (!teacherForm.name.trim()) {
      toast.error("Teacher name is required.");
      return;
    }

    const specsArray = teacherForm.specialization
      ? teacherForm.specialization.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    const batchesArray = teacherForm.assignedBatches
      ? teacherForm.assignedBatches.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

    let updatedList;
    if (teacherModal.isEditing && teacherModal.data) {
      updatedList = teachers.map((t) => {
        if (t.id === teacherModal.data.id) {
          return {
            ...t,
            ...teacherForm,
            specialization: specsArray,
            assignedBatches: batchesArray,
            monthlySalary: Number(teacherForm.monthlySalary || 0),
          };
        }
        return t;
      });
      toast.success("Teacher profile updated.");
    } else {
      const newTeacher = {
        id: `tea-${Date.now()}`,
        tuitionId: currentInstitute.id,
        ...teacherForm,
        specialization: specsArray,
        assignedBatches: batchesArray,
        monthlySalary: Number(teacherForm.monthlySalary || 0),
        salaryStatus: "PENDING",
        status: "ACTIVE",
        totalStudents: 0,
      };
      updatedList = [newTeacher, ...teachers];
      toast.success("New faculty member onboarded.");
    }

    setTeachers(updatedList);
    saveStoredTeachers(updatedList);
    setTeacherModal({ open: false, isEditing: false, data: null });
  };

  const handleDeleteTeacher = async (teacherId) => {
    const confirm = window.confirm("Are you sure you want to remove this faculty record?");
    if (!confirm) return;
    try {
      await api.delete(API_PATHS.tuition.teacherById(teacherId));
      setTeachers((current) => current.filter((t) => t.id !== teacherId));
      toast.info("Teacher record removed.");
    } catch (error) { toast.error(error?.message || "Unable to remove this teacher."); }
  };

  // -------------------------------------------------------------
  // SALARY DISBURSEMENT HANDLER
  // -------------------------------------------------------------
  const openPaySalaryModal = (teacher) => {
    setSalaryForm({
      month: "",
      amount: teacher.monthlySalary || "",
      paymentMode: "Bank NEFT",
      referenceId: "",
    });
    setSalaryModal({ open: true, teacher });
  };

  const handleDisburseSalary = (e) => {
    e.preventDefault();
    if (!salaryModal.teacher) return;

    const voucher = {
      id: `sal-${Date.now()}`,
      tuitionId: currentInstitute.id,
      teacherId: salaryModal.teacher.id,
      teacherName: salaryModal.teacher.name,
      month: salaryForm.month,
      amount: Number(salaryForm.amount),
      disbursedOn: new Date().toISOString().split("T")[0],
      paymentMode: salaryForm.paymentMode,
      referenceId: salaryForm.referenceId,
      status: "COMPLETED",
    };

    const nextSalaries = [voucher, ...salaries];
    setSalaries(nextSalaries);
    saveStoredSalaries(nextSalaries);

    // Update teacher's status to paid
    const nextTeachers = teachers.map((t) => {
      if (t.id === salaryModal.teacher.id) {
        return { ...t, salaryStatus: "PAID", lastPaidDate: new Date().toISOString().split("T")[0] };
      }
      return t;
    });
    setTeachers(nextTeachers);
    saveStoredTeachers(nextTeachers);

    setSalaryModal({ open: false, teacher: null });
    toast.success(`Salary of ₹${voucher.amount.toLocaleString("en-IN")} disbursed to ${voucher.teacherName}!`);
  };

  // -------------------------------------------------------------
  // PARENT FEE REMINDER HANDLER
  // -------------------------------------------------------------
  const openReminderModal = (student) => {
    const template = `Dear ${student.parentName || "Parent"}, this is a reminder from ${currentInstitute?.name} that the pending tuition fee of ₹${(student.remainingFee || 0).toLocaleString("en-IN")} for ${student.name} (${student.rollNo}) is due on ${student.dueDate || "this month"}. Kindly clear the balance to ensure uninterrupted classes. Thank you!`;
    setReminderModal({ open: true, student, messageText: template });
  };

  const handleSendWhatsAppReminder = () => {
    if (!reminderModal.student?.parentMobile) {
      toast.error("Parent mobile number is not available.");
      return;
    }
    const cleanPhone = reminderModal.student.parentMobile.replace(/[^0-9]/g, "");
    const encoded = encodeURIComponent(reminderModal.messageText);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encoded}`;
    window.open(whatsappUrl, "_blank");
    toast.success(`WhatsApp reminder opened for ${reminderModal.student.parentName}!`);
    setReminderModal({ open: false, student: null, messageText: "" });
  };

  const handleSendSmsReminder = () => {
    toast.success(`SMS reminder queued & sent to ${reminderModal.student?.parentMobile} via ClassNX Gateway!`);
    setReminderModal({ open: false, student: null, messageText: "" });
  };

  // -------------------------------------------------------------
  // ONLINE CLASS SCHEDULER HANDLER
  // -------------------------------------------------------------
  const handleScheduleClass = (e) => {
    e.preventDefault();
    if (!classForm.topic.trim()) {
      toast.error("Class topic is required.");
      return;
    }

    const meetingId = `NX-${classForm.subject.substring(0, 4).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`;

    const newClass = {
      id: `cls-${Date.now()}`,
      tuitionId: currentInstitute.id,
      meetingId,
      topic: classForm.topic.trim(),
      subject: classForm.subject,
      batch: classForm.batch,
      teacherName: classForm.teacherName,
      scheduledDate: classForm.scheduledDate,
      startTime: classForm.startTime,
      endTime: classForm.endTime,
      status: "scheduled",
      activeParticipantsCount: 0,
      attendees: [],
    };

    const nextClasses = [newClass, ...onlineClasses];
    setOnlineClasses(nextClasses);
    saveStoredOnlineClasses(nextClasses);
    setScheduleClassModal({ open: false });
    toast.success(`Online lecture scheduled! Meeting ID: ${meetingId}`);
  };

  const handleDeleteOnlineClass = async (onlineClass) => {
    if (onlineClass.status === "live") {
      toast.error("End the live meeting before deleting it");
      return;
    }
    if (!window.confirm(`Delete ${onlineClass.topic || "this online class"}? Attendance records will be preserved.`)) return;

    try {
      await api.delete(API_PATHS.tuition.deleteOnlineClass(onlineClass.meetingId));
      setOnlineClasses((current) => current.filter((item) => item.meetingId !== onlineClass.meetingId));
      toast.success("Online class deleted.");
    } catch (error) {
      toast.error(error?.message || "Unable to delete the online class.");
    }
  };

  // -------------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------------
  if (activeStudioClass) {
    return (
      <ClassroomManagementStudio
        roomCode={activeStudioClass.meetingId}
        scheduledClass={activeStudioClass}
        isHost={true}
        facultyName={activeTenant?.instituteName || activeTenant?.name || "Institute Admin"}
        onClose={() => {
          setActiveStudioClass(null);
          if (onlineClassesEnabled) {
            api
              .get(API_PATHS.tuition.onlineClasses())
              .then((res) => {
                const payload = res?.data?.data ?? res?.data;
                const list = Array.isArray(payload) ? payload : payload?.rooms || payload?.classes || [];
                setOnlineClasses(normalizeVisibleOnlineClasses(list));
              })
              .catch(() => {});
          }
        }}
      />
    );
  }

  return (
    <div className={`min-h-screen pb-16 pt-4 px-3 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Top Header & Switcher */}
        <div className={`rounded-3xl border p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-tr from-sky-600 to-indigo-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-sky-600/20">
              <FaBuilding />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-sky-100 dark:bg-sky-950/60 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-sky-700 dark:text-sky-300">
                  Tuition Founder Portal
                </span>
                <span className="rounded-full bg-emerald-100 dark:bg-emerald-950/60 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  {currentInstitute?.plan || "GROWTH"} Plan
                </span>
              </div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100 mt-1">
                {currentInstitute?.name}
              </h1>
              <p className="text-xs text-slate-500">
                Founder: {currentInstitute?.ownerName} • {currentInstitute?.city}
              </p>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {enabledModules[TUITION_MODULE_KEYS.STUDENT_MANAGEMENT] && (
              <button
                type="button"
                onClick={openNewStudentModal}
                className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:bg-sky-700 transition"
              >
                <FaPlus /> New Admission
              </button>
            )}

            {enabledModules[TUITION_MODULE_KEYS.TEACHER_MANAGEMENT] && (
              <button
                type="button"
                onClick={openNewTeacherModal}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
              >
                <FaChalkboardTeacher /> Add Faculty
              </button>
            )}

            {enabledModules[TUITION_MODULE_KEYS.ONLINE_CLASSES] && (
              <button
                type="button"
                onClick={() => navigate("/tuition/classes/new")}
                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm hover:opacity-90 transition"
              >
                <FaVideo /> Schedule Class
              </button>
            )}

            <Link
              to="/join-class"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sky-500/40 bg-sky-500/10 px-3.5 py-2 text-xs font-bold text-sky-500 hover:bg-sky-500/20 transition"
            >
              <FaExternalLinkAlt /> Join Direct Class UI
            </Link>
          </div>
        </div>

        {/* Dynamic Module Nav Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
          <button
            type="button"
            onClick={() => setActiveTab("analytics")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "analytics" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
          >
            <FaChartLine /> Overview & Financials
          </button>

          {enabledModules[TUITION_MODULE_KEYS.STUDENT_MANAGEMENT] && (
            <button
              type="button"
              onClick={() => setActiveTab("students")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "students" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              <FaUserGraduate /> Students & Admissions ({students.length})
            </button>
          )}

          {enabledModules[TUITION_MODULE_KEYS.TEACHER_MANAGEMENT] && (
            <button
              type="button"
              onClick={() => setActiveTab("teachers")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "teachers" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              <FaChalkboardTeacher /> Faculty & Staff ({teachers.length})
            </button>
          )}

          {enabledModules[TUITION_MODULE_KEYS.FEES_FINANCIALS] && (
            <button
              type="button"
              onClick={() => setActiveTab("financials")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "financials" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              <FaFileInvoiceDollar /> Fee & Salary Ledger
            </button>
          )}

          {enabledModules[TUITION_MODULE_KEYS.ONLINE_CLASSES] && (
            <button
              type="button"
              onClick={() => setActiveTab("classes")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "classes" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              <FaVideo /> Live Online Classes ({onlineClasses.length})
            </button>
          )}
        </div>

        {/* -------------------------------------------------------------
            TAB 1: OVERVIEW & FINANCIAL ANALYTICS
        ------------------------------------------------------------- */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider">
                  <span>Enrolled Students</span>
                  <FaUserGraduate className="text-sky-500 text-base" />
                </div>
                <div className="mt-2 text-2xl font-black">{students.length}</div>
                <p className="mt-1 text-xs text-emerald-500 font-semibold">+12% this month</p>
              </div>

              <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider">
                  <span>Total Fees Collected</span>
                  <FaMoneyBillWave className="text-emerald-500 text-base" />
                </div>
                <div className="mt-2 text-2xl font-black text-emerald-600 dark:text-emerald-400">
                  ₹{financialSummary.totalCollected.toLocaleString("en-IN")}
                </div>
                <p className="mt-1 text-xs text-slate-500">₹{financialSummary.totalPending.toLocaleString("en-IN")} pending</p>
              </div>

              <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider">
                  <span>Faculty Salaries Paid</span>
                  <FaChalkboardTeacher className="text-indigo-500 text-base" />
                </div>
                <div className="mt-2 text-2xl font-black text-indigo-600 dark:text-indigo-400">
                  ₹{financialSummary.totalSalaryPaid.toLocaleString("en-IN")}
                </div>
                <p className="mt-1 text-xs text-slate-500">{teachers.length} Active Faculty</p>
              </div>

              <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider">
                  <span>Net Operating Margin</span>
                  <FaChartLine className="text-violet-500 text-base" />
                </div>
                <div className="mt-2 text-2xl font-black text-violet-600 dark:text-violet-400">
                  ₹{financialSummary.netTuitionMargin.toLocaleString("en-IN")}
                </div>
                <p className="mt-1 text-xs text-emerald-500 font-semibold">Healthy Cashflow</p>
              </div>
            </div>

            {/* Financial Visual Charts Grid */}
            <div className="grid md:grid-cols-12 gap-6">
              {/* Fee Collection Visual Bar Chart */}
              <div className={`md:col-span-7 rounded-3xl border p-6 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold">Student Fee Collections vs Targets</h3>
                    <p className="text-xs text-slate-500">Monthly breakdown of student fee income</p>
                  </div>
                  <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-500">
                    {financialSummary.totalBilled > 0
                      ? `${Math.round((financialSummary.totalCollected / financialSummary.totalBilled) * 100)}% Collection Rate`
                      : "No collection data"}
                  </span>
                </div>

                <div className="flex h-48 w-full items-center justify-center rounded-2xl border border-dashed border-slate-300 px-4 text-center text-xs text-slate-500 dark:border-slate-700">
                  Monthly collection history will appear when fee transactions are available.
                </div>
              </div>

              {/* Profit & Expense Donut Ring Breakdown */}
              <div className={`md:col-span-5 rounded-3xl border p-6 shadow-sm flex flex-col justify-between ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <div>
                  <h3 className="text-base font-bold">Revenue & Cost Structure</h3>
                  <p className="text-xs text-slate-500">Fee income allocation</p>
                </div>

                <div className="my-4 flex items-center justify-center">
                  <div className="w-full space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-indigo-500" />
                      <span className="text-slate-500">Faculty Salaries: <strong>{financialSummary.salaryPercentage === null ? "No data" : `${financialSummary.salaryPercentage.toFixed(1)}%`}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-emerald-500" />
                      <span className="text-slate-500">Margin after recorded salaries: <strong>{financialSummary.marginPercentage === null ? "No data" : `${financialSummary.marginPercentage.toFixed(1)}%`}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full bg-slate-300 dark:bg-slate-700" />
                      <span className="text-slate-500">Overheads: <strong>Awaiting expense API</strong></span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-3 text-xs text-slate-500">
                  Balance after recorded faculty salaries: <strong className="text-emerald-500">₹{financialSummary.netTuitionMargin.toLocaleString("en-IN")}</strong>. Overheads are not included.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 2: STUDENT ADMISSIONS & RECORDS (CRUD)
        ------------------------------------------------------------- */}
        {activeTab === "students" && (
          <div className="space-y-4">
            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-xs" />
                  <input
                    type="text"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    placeholder="Search student or parent..."
                    className={`w-full rounded-2xl border pl-9 pr-4 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
                  />
                </div>

                <select
                  value={studentBatchFilter}
                  onChange={(e) => setStudentBatchFilter(e.target.value)}
                  className={`rounded-2xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-200" : "border-slate-200 bg-white text-slate-700"}`}
                >
                  <option value="ALL">All Batches</option>
                  {batchOptions.map((batch) => <option key={batch} value={batch}>{batch}</option>)}
                </select>
              </div>

              <button
                type="button"
                onClick={openNewStudentModal}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2.5 text-xs font-bold text-white shadow-md hover:bg-sky-700 transition"
              >
                <FaPlus /> Admit New Student
              </button>
            </div>

            {/* Students Table */}
            <div className={`overflow-x-auto rounded-3xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <table className="w-full text-left text-xs">
                <thead className={`border-b text-[11px] font-bold uppercase tracking-wider text-slate-400 ${isDarkMode ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                  <tr>
                    <th className="px-4 py-3.5">Student / Roll</th>
                    <th className="px-4 py-3.5">Batch & Grade</th>
                    <th className="px-4 py-3.5">Parent Contact</th>
                    <th className="px-4 py-3.5">Fee Status</th>
                    <th className="px-4 py-3.5">Attendance</th>
                    <th className="px-4 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        No students found matching your filter criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-500/5 transition">
                        <td className="px-4 py-3">
                          <p className="font-bold text-slate-900 dark:text-slate-100">{s.name}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{s.rollNo} • {s.mobile}</p>
                        </td>

                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-700 dark:text-slate-300">{s.batch}</p>
                          <p className="text-[10px] text-slate-400">
                            {Array.isArray(s.subjects) ? s.subjects.join(", ") : s.subjects}
                          </p>
                        </td>

                        <td className="px-4 py-3">
                          <p className="font-semibold">{s.parentName} ({s.parentRelation || "Parent"})</p>
                          <p className="text-[10px] text-slate-400">{s.parentMobile}</p>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${s.feeStatus === "PAID" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : s.feeStatus === "PARTIAL" ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" : "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300"}`}>
                              {s.feeStatus}
                            </span>
                            <span className="font-mono text-xs">
                              ₹{(s.paidFee || 0).toLocaleString("en-IN")} / ₹{(s.totalFee || 0).toLocaleString("en-IN")}
                            </span>
                          </div>
                          {s.remainingFee > 0 && (
                            <p className="text-[10px] text-rose-500 font-semibold mt-0.5">
                              Due: ₹{s.remainingFee.toLocaleString("en-IN")} by {s.dueDate}
                            </p>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-16 bg-slate-200 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-sky-500 h-full rounded-full"
                                style={{ width: `${Number(s.attendanceRate || 0)}%` }}
                              />
                            </div>
                            <span className="font-bold text-[11px]">{Number(s.attendanceRate || 0)}%</span>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {enabledModules[TUITION_MODULE_KEYS.PARENT_REMINDERS] && s.remainingFee > 0 && (
                              <button
                                type="button"
                                onClick={() => openReminderModal(s)}
                                className="rounded-lg p-1.5 text-xs text-amber-600 hover:bg-amber-500/10 transition"
                                title="Send Fee Reminder Message to Parent"
                              >
                                <FaWhatsapp className="text-sm" />
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => setViewStudentModal({ open: true, student: s })}
                              className="rounded-lg p-1.5 text-xs text-sky-600 hover:bg-sky-500/10 transition"
                              title="View Full Profile"
                            >
                              <FaEye />
                            </button>

                            <button
                              type="button"
                              onClick={() => openEditStudentModal(s)}
                              className="rounded-lg p-1.5 text-xs text-slate-500 hover:bg-slate-500/10 transition"
                              title="Edit Record"
                            >
                              <FaEdit />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteStudent(s.id)}
                              className="rounded-lg p-1.5 text-xs text-rose-600 hover:bg-rose-500/10 transition"
                              title="Delete Record"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 3: FACULTY & TEACHER MANAGEMENT (CRUD)
        ------------------------------------------------------------- */}
        {activeTab === "teachers" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="relative w-full sm:w-72">
                <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-xs" />
                <input
                  type="text"
                  value={teacherSearch}
                  onChange={(e) => setTeacherSearch(e.target.value)}
                  placeholder="Search faculty name or subject..."
                  className={`w-full rounded-2xl border pl-9 pr-4 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
                />
              </div>

              <button
                type="button"
                onClick={openNewTeacherModal}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2.5 text-xs font-bold text-white shadow-md hover:bg-sky-700 transition"
              >
                <FaPlus /> Add Faculty Member
              </button>
            </div>

            {/* Teachers Card Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTeachers.map((t) => (
                <div
                  key={t.id}
                  className={`rounded-3xl border p-5 shadow-sm flex flex-col justify-between ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
                >
                  <div>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center font-bold text-lg">
                          {t.name.charAt(0)}
                        </div>
                        <div>
                          <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100">{t.name}</h4>
                          <p className="text-[11px] text-slate-400">{t.qualification || "Faculty"}</p>
                        </div>
                      </div>

                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${t.salaryStatus === "PAID" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"}`}>
                        Salary {t.salaryStatus}
                      </span>
                    </div>

                    {/* Specializations & Batches */}
                    <div className="mt-4 space-y-2 text-xs">
                      <div>
                        <span className="text-[10px] font-bold uppercase text-slate-400">Specialization:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(Array.isArray(t.specialization) ? t.specialization : [t.specialization]).map((spec, i) => (
                            <span key={i} className="rounded-lg bg-sky-500/10 px-2 py-0.5 text-[11px] font-semibold text-sky-600 dark:text-sky-400">
                              {spec}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <span className="text-[10px] font-bold uppercase text-slate-400">Assigned Batches:</span>
                        <p className="text-slate-600 dark:text-slate-300 font-semibold mt-0.5">
                          {Array.isArray(t.assignedBatches) ? t.assignedBatches.join(", ") : t.assignedBatches}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-slate-400">Monthly Compensation:</span>
                        <strong className="text-slate-900 dark:text-slate-100 text-sm">
                          ₹{(t.monthlySalary || 0).toLocaleString("en-IN")}/mo
                        </strong>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openPaySalaryModal(t)}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 transition"
                    >
                      <FaMoneyBillWave /> Pay Salary
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEditTeacherModal(t)}
                        className="rounded-lg p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        title="Edit Teacher"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTeacher(t.id)}
                        className="rounded-lg p-2 text-rose-500 hover:text-rose-600"
                        title="Delete Teacher"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 4: FEES & SALARY DISBURSEMENT LEDGER
        ------------------------------------------------------------- */}
        {activeTab === "financials" && (
          <div className="space-y-6">
            {/* Salary Disbursements Table */}
            <div className={`rounded-3xl border p-6 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold">Faculty Salary Disbursements</h3>
                  <p className="text-xs text-slate-500">Record of compensation paid to tuition teachers</p>
                </div>
                <span className="text-xs font-bold text-slate-500">
                  Total Disbursed: ₹{financialSummary.totalSalaryPaid.toLocaleString("en-IN")}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className={`border-b text-[11px] font-bold uppercase tracking-wider text-slate-400 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                    <tr>
                      <th className="py-2.5">Teacher Name</th>
                      <th className="py-2.5">Month</th>
                      <th className="py-2.5">Disbursed On</th>
                      <th className="py-2.5">Payment Mode</th>
                      <th className="py-2.5">Reference ID</th>
                      <th className="py-2.5 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {salaries.map((sal) => (
                      <tr key={sal.id}>
                        <td className="py-3 font-bold text-slate-900 dark:text-slate-100">{sal.teacherName}</td>
                        <td className="py-3 text-slate-500">{sal.month}</td>
                        <td className="py-3 text-slate-500">{sal.disbursedOn}</td>
                        <td className="py-3 font-semibold">{sal.paymentMode}</td>
                        <td className="py-3 font-mono text-[11px] text-slate-400">{sal.referenceId}</td>
                        <td className="py-3 text-right font-black text-emerald-600 dark:text-emerald-400 text-sm">
                          ₹{sal.amount.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 5: LIVE ONLINE CLASSES & DIRECT JOIN LINKS
        ------------------------------------------------------------- */}
        {activeTab === "classes" && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-bold">Scheduled & Live Online Lectures</h3>
                <p className="text-xs text-slate-500">
                  Share meeting IDs with students for one-click direct join without login
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/tuition/classes/new")}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2.5 text-xs font-bold text-white shadow-md hover:opacity-90 transition"
              >
                <FaVideo /> Schedule New Lecture
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {onlineClasses.map((cls) => (
                <div
                  key={cls.id}
                  className={`rounded-3xl border p-5 shadow-sm flex flex-col justify-between ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${cls.status === "live" ? "bg-emerald-500/20 text-emerald-500 animate-pulse" : cls.status === "scheduled" ? "bg-sky-500/20 text-sky-500" : "bg-slate-500/20 text-slate-400"}`}>
                        {cls.status === "live" ? "● LIVE CLASSROOM" : cls.status.toUpperCase()}
                      </span>
                      <span className="text-xs font-bold text-slate-400">{cls.subject}</span>
                    </div>

                    <h4 className="font-bold text-base mt-2 text-slate-900 dark:text-slate-100">{cls.topic}</h4>
                    <p className="text-xs text-slate-500 mt-1">Batch: {cls.batch} • {cls.teacherName}</p>

                    <div className={`mt-4 rounded-2xl border p-3 text-xs flex items-center justify-between ${isDarkMode ? "border-slate-800 bg-slate-950/60" : "border-slate-100 bg-slate-50"}`}>
                      <div>
                        <span className="text-[10px] font-bold uppercase text-slate-400">Meeting ID:</span>
                        <p className="font-mono font-bold text-sm text-sky-500">{cls.meetingId}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(cls.meetingId);
                          toast.success(`Meeting code ${cls.meetingId} copied to clipboard!`);
                        }}
                        className="rounded-lg border border-slate-300 dark:border-slate-700 p-2 text-xs hover:bg-slate-200 dark:hover:bg-slate-800 transition"
                        title="Copy Code"
                      >
                        <FaCopy />
                      </button>
                    </div>

                    <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5">
                      <FaClock /> Scheduled: {cls.scheduledDate} from {cls.startTime} to {cls.endTime}
                    </div>
                  </div>

                  <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setActiveStudioClass(cls)}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-sky-700 transition shadow"
                    >
                      <FaPlay /> Enter Room
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const directUrl = `${window.location.origin}/classroom/join/${cls.meetingId}`;
                        navigator.clipboard?.writeText(directUrl);
                        toast.success("Direct Join Link copied to clipboard!");
                      }}
                      className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-sky-500 transition"
                    >
                      <FaShareAlt /> Share Link
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeleteOnlineClass(cls)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-500 hover:text-rose-600 transition"
                      title={cls.status === "live" ? "End the live meeting before deleting it" : "Delete online class"}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Attendance Logs Recorded by Direct Join */}
            {attendanceLogs.length > 0 && (
              <div className={`rounded-3xl border p-6 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <h4 className="text-sm font-bold flex items-center gap-2 mb-3">
                  <FaCheckCircle className="text-emerald-500" /> Recent Direct Meeting Join Attendance Records
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="border-b text-[10px] uppercase font-bold text-slate-400">
                      <tr>
                        <th className="py-2">Student Name</th>
                        <th className="py-2">Meeting ID</th>
                        <th className="py-2">Topic</th>
                        <th className="py-2">Duration</th>
                        <th className="py-2">Joined / Left</th>
                        <th className="py-2 text-right">DB Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {attendanceLogs.slice(0, 5).map((log) => (
                        <tr key={log.id}>
                          <td className="py-2.5 font-bold">{log.studentName}</td>
                          <td className="py-2.5 font-mono text-sky-500">{log.meetingId}</td>
                          <td className="py-2.5 text-slate-500">{log.topic}</td>
                          <td className="py-2.5 font-bold text-emerald-500">{log.durationFormatted || `${log.durationMinutes}m`}</td>
                          <td className="py-2.5 text-[11px] text-slate-400">
                            {new Date(log.joinedAt).toLocaleTimeString()} - {new Date(log.leftAt).toLocaleTimeString()}
                          </td>
                          <td className="py-2.5 text-right font-bold text-emerald-600 dark:text-emerald-400">
                            LOGGED
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* -------------------------------------------------------------
          MODAL: STUDENT ADMISSION (CREATE / EDIT)
      ------------------------------------------------------------- */}
      {studentModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-xl font-black">
              {studentModal.isEditing ? "Edit Student Admission" : "New Student Admission"}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Fill in student details, batch enrollment, parent contact, and fee agreement.
            </p>

            <form onSubmit={handleSaveStudent} className="mt-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Student Full Name *</label>
                  <input
                    type="text"
                    required
                    value={studentForm.name}
                    onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Roll / Admission ID *</label>
                  <input
                    type="text"
                    required
                    value={studentForm.rollNo}
                    onChange={(e) => setStudentForm({ ...studentForm, rollNo: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Batch / Program</label>
                  <input
                    type="text"
                    value={studentForm.batch}
                    onChange={(e) => setStudentForm({ ...studentForm, batch: e.target.value })}
                    placeholder="Enter batch or program"
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Grade / Level</label>
                  <input
                    type="text"
                    value={studentForm.grade}
                    onChange={(e) => setStudentForm({ ...studentForm, grade: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Enrolled Subjects (comma separated)</label>
                <input
                  type="text"
                  value={studentForm.subjects}
                  onChange={(e) => setStudentForm({ ...studentForm, subjects: e.target.value })}
                  placeholder="e.g. Physics, Mathematics, Chemistry"
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              {/* Parent Details */}
              <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
                <p className="text-xs font-bold uppercase text-sky-500 mb-3">Parent / Guardian Information</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Parent Name</label>
                    <input
                      type="text"
                      value={studentForm.parentName}
                      onChange={(e) => setStudentForm({ ...studentForm, parentName: e.target.value })}
                      className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Parent Mobile (for Reminders)</label>
                    <input
                      type="text"
                      value={studentForm.parentMobile}
                      onChange={(e) => setStudentForm({ ...studentForm, parentMobile: e.target.value })}
                      placeholder="+91 98765 43210"
                      className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                    />
                  </div>
                </div>
              </div>

              {/* Fee Agreement */}
              <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
                <p className="text-xs font-bold uppercase text-emerald-500 mb-3">Tuition Fee Agreement</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Total Fee (₹)</label>
                    <input
                      type="number"
                      value={studentForm.totalFee}
                      onChange={(e) => setStudentForm({ ...studentForm, totalFee: e.target.value })}
                      className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Paid Amount (₹)</label>
                    <input
                      type="number"
                      value={studentForm.paidFee}
                      onChange={(e) => setStudentForm({ ...studentForm, paidFee: e.target.value })}
                      className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Next Due Date</label>
                    <input
                      type="date"
                      value={studentForm.dueDate}
                      onChange={(e) => setStudentForm({ ...studentForm, dueDate: e.target.value })}
                      className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setStudentModal({ open: false, isEditing: false, data: null })}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-sky-600 px-5 py-2 text-xs font-bold text-white hover:bg-sky-700 transition"
                >
                  {studentModal.isEditing ? "Save Changes" : "Confirm Admission"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------
          MODAL: TEACHER FACULTY (CREATE / EDIT)
      ------------------------------------------------------------- */}
      {teacherModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-xl font-black">
              {teacherModal.isEditing ? "Edit Faculty Profile" : "Onboard New Faculty"}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Add teacher details, subject specializations, and monthly compensation.
            </p>

            <form onSubmit={handleSaveTeacher} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Faculty Full Name *</label>
                <input
                  type="text"
                  required
                  value={teacherForm.name}
                  onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })}
                  placeholder="Enter faculty name"
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Email Address</label>
                  <input
                    type="email"
                    value={teacherForm.email}
                    onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={teacherForm.phone}
                    onChange={(e) => setTeacherForm({ ...teacherForm, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Subject Specializations</label>
                <input
                  type="text"
                  value={teacherForm.specialization}
                  onChange={(e) => setTeacherForm({ ...teacherForm, specialization: e.target.value })}
                  placeholder="e.g. Physics, Mechanics, Thermodynamics"
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Assigned Batches</label>
                <input
                  type="text"
                  value={teacherForm.assignedBatches}
                  onChange={(e) => setTeacherForm({ ...teacherForm, assignedBatches: e.target.value })}
                  placeholder="Enter assigned batches"
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Monthly Salary (₹)</label>
                  <input
                    type="number"
                    value={teacherForm.monthlySalary}
                    onChange={(e) => setTeacherForm({ ...teacherForm, monthlySalary: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Qualification / Experience</label>
                  <input
                    type="text"
                    value={teacherForm.qualification}
                    onChange={(e) => setTeacherForm({ ...teacherForm, qualification: e.target.value })}
                    placeholder="M.Tech IIT, 8 Yrs Exp"
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setTeacherModal({ open: false, isEditing: false, data: null })}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-sky-600 px-5 py-2 text-xs font-bold text-white hover:bg-sky-700 transition"
                >
                  {teacherModal.isEditing ? "Update Faculty" : "Add Faculty"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------
          MODAL: PAY SALARY TO TEACHER
      ------------------------------------------------------------- */}
      {salaryModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-md rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-lg font-black flex items-center gap-2">
              <FaMoneyBillWave className="text-emerald-500" /> Disburse Faculty Salary
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Disbursing compensation to <strong>{salaryModal.teacher?.name}</strong>.
            </p>

            <form onSubmit={handleDisburseSalary} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Salary Month / Period</label>
                <input
                  type="text"
                  value={salaryForm.month}
                  onChange={(e) => setSalaryForm({ ...salaryForm, month: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Payment Amount (₹)</label>
                <input
                  type="number"
                  value={salaryForm.amount}
                  onChange={(e) => setSalaryForm({ ...salaryForm, amount: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Payment Mode</label>
                <select
                  value={salaryForm.paymentMode}
                  onChange={(e) => setSalaryForm({ ...salaryForm, paymentMode: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                >
                  <option value="Bank NEFT">Bank NEFT Transfer</option>
                  <option value="UPI">UPI / Instant Bank Transfer</option>
                  <option value="Bank IMPS">Bank IMPS</option>
                  <option value="Cheque">Account Payee Cheque</option>
                  <option value="Cash">Cash Voucher</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Transaction Reference / Voucher ID</label>
                <input
                  type="text"
                  value={salaryForm.referenceId}
                  onChange={(e) => setSalaryForm({ ...salaryForm, referenceId: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setSalaryModal({ open: false, teacher: null })}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-emerald-600 px-5 py-2 text-xs font-bold text-white hover:bg-emerald-700 transition"
                >
                  Confirm & Disburse
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------
          MODAL: SEND PARENT FEE REMINDER
      ------------------------------------------------------------- */}
      {reminderModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-lg font-black flex items-center gap-2">
              <FaWhatsapp className="text-emerald-500" /> Send Fee Reminder Message
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Recipient: <strong>{reminderModal.student?.parentName}</strong> ({reminderModal.student?.parentMobile})
            </p>

            <div className="mt-4">
              <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
                Customizable Message Preview:
              </label>
              <textarea
                rows="5"
                value={reminderModal.messageText}
                onChange={(e) => setReminderModal({ ...reminderModal, messageText: e.target.value })}
                className={`w-full rounded-2xl border p-3 text-xs leading-relaxed font-mono focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-slate-50 text-slate-900"}`}
              />
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3 pt-3">
              <button
                type="button"
                onClick={() => setReminderModal({ open: false, student: null, messageText: "" })}
                className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendSmsReminder}
                className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-2 text-xs font-bold text-sky-500 hover:bg-sky-500/20 transition"
              >
                Send SMS
              </button>
              <button
                type="button"
                onClick={handleSendWhatsAppReminder}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-5 py-2 text-xs font-bold text-white hover:bg-emerald-700 transition"
              >
                <FaWhatsapp /> Send via WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------
          MODAL: SCHEDULE ONLINE CLASS
      ------------------------------------------------------------- */}
      {scheduleClassModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-xl font-black flex items-center gap-2">
              <FaVideo className="text-indigo-500" /> Schedule Online Lecture
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              ClassNX generates a unique Meeting ID for direct no-login student entry.
            </p>

            <form onSubmit={handleScheduleClass} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Lecture Topic *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter lecture topic"
                  value={classForm.topic}
                  onChange={(e) => setClassForm({ ...classForm, topic: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Subject</label>
                  <input
                    type="text"
                    value={classForm.subject}
                    onChange={(e) => setClassForm({ ...classForm, subject: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Batch</label>
                  <input
                    type="text"
                    value={classForm.batch}
                    onChange={(e) => setClassForm({ ...classForm, batch: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Assigned Teacher</label>
                <input
                  type="text"
                  value={classForm.teacherName}
                  onChange={(e) => setClassForm({ ...classForm, teacherName: e.target.value })}
                  className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                />
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Date</label>
                  <input
                    type="text"
                    value={classForm.scheduledDate}
                    onChange={(e) => setClassForm({ ...classForm, scheduledDate: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Start Time</label>
                  <input
                    type="time"
                    value={classForm.startTime}
                    onChange={(e) => setClassForm({ ...classForm, startTime: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">End Time</label>
                  <input
                    type="time"
                    value={classForm.endTime}
                    onChange={(e) => setClassForm({ ...classForm, endTime: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setScheduleClassModal({ open: false })}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-violet-600 px-5 py-2 text-xs font-bold text-white hover:bg-violet-700 transition"
                >
                  Create & Schedule Lecture
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -------------------------------------------------------------
          MODAL: VIEW FULL STUDENT PROFILE
      ------------------------------------------------------------- */}
      {viewStudentModal.open && viewStudentModal.student && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <div>
                <h3 className="text-xl font-black">{viewStudentModal.student.name}</h3>
                <p className="text-xs text-slate-400 font-mono">Roll: {viewStudentModal.student.rollNo}</p>
              </div>
              <button
                type="button"
                onClick={() => setViewStudentModal({ open: false, student: null })}
                className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="mt-4 space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Batch:</span>
                  <p className="font-semibold">{viewStudentModal.student.batch}</p>
                </div>
                <div>
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Grade:</span>
                  <p className="font-semibold">{viewStudentModal.student.grade}</p>
                </div>
              </div>

              <div>
                <span className="text-slate-400 font-bold uppercase text-[10px]">Parent:</span>
                <p className="font-semibold">
                  {viewStudentModal.student.parentName} ({viewStudentModal.student.parentMobile})
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 p-3 rounded-2xl bg-slate-100 dark:bg-slate-950/50">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase">Total Fee</span>
                  <p className="font-bold text-sm">₹{viewStudentModal.student.totalFee?.toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <span className="text-[10px] text-emerald-500 uppercase">Paid</span>
                  <p className="font-bold text-sm text-emerald-500">₹{viewStudentModal.student.paidFee?.toLocaleString("en-IN")}</p>
                </div>
                <div>
                  <span className="text-[10px] text-rose-500 uppercase">Remaining</span>
                  <p className="font-bold text-sm text-rose-500">₹{viewStudentModal.student.remainingFee?.toLocaleString("en-IN")}</p>
                </div>
              </div>

              {viewStudentModal.student.recentMarks && viewStudentModal.student.recentMarks.length > 0 && (
                <div>
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Recent Assessment Marks:</span>
                  <div className="space-y-1.5 mt-1.5">
                    {viewStudentModal.student.recentMarks.map((m, i) => (
                      <div key={i} className="flex justify-between items-center p-2 rounded-xl border border-slate-200 dark:border-slate-800 text-[11px]">
                        <span>{m.testName}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold">{m.marks}/{m.maxMarks}</span>
                          <span className="rounded bg-sky-500/10 px-1.5 py-0.5 font-bold text-sky-600 dark:text-sky-400">{m.grade}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setViewStudentModal({ open: false, student: null })}
                className="rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-5 py-2 text-xs font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
